import {
  EstimatedTaxWaterfall,
  EstimatedTaxEnrollment,
  EstimatedTaxSettings,
  UpdateSettingsPayload,
  IRSPayment,
  LogPaymentPayload,
  MonthlyManualEntry,
  SetMonthlyEntryPayload,
  S174Entry,
  AddS174EntryPayload,
  UpdateS174EntryPayload,
  SyncConfig,
  SetSyncConfigPayload,
  ClearSyncConfigPayload,
  IncomeStatementUploadResult,
  TaxAdjustment,
  MonthlyAdjustmentBreakdown,
  CreateAdjustmentPayload,
  UpdateAdjustmentPayload,
  FinancialAdjustment,
  CreateFinancialAdjustmentPayload,
  UpdateFinancialAdjustmentPayload,
  TaxBenefit,
  TaxBenefitsResponse,
  AddBenefitEntryPayload,
  UploadTaxReturnResult,
} from "types/Models/estimatedTax";
import { emptyApi } from "./emptyApi";

const BASE = (entityId: string) =>
  `/api/inkle/estimated_tax/entity/${entityId}`;

const extendedApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    // ── Waterfall (full calculation including enrollment check) ───────────
    getEstimatedTaxWaterfall: build.query<
      EstimatedTaxWaterfall,
      { entityId: string; year: string }
    >({
      query: ({ entityId, year }) => ({
        url: `${BASE(entityId)}/waterfall/?year=${year}`,
      }),
      providesTags: ["ESTIMATED_TAX_WATERFALL"],
      // The waterfall run persists benefit usage, so Tax Benefits must refetch
      // after it settles or it can show stale "used" amounts.
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          console.error(error);
        } finally {
          dispatch(emptyApi.util.invalidateTags(["ESTIMATED_TAX_BENEFITS"]));
        }
      },
    }),

    // ── Enrollment ────────────────────────────────────────────────────────
    getEnrollment: build.query<EstimatedTaxEnrollment, { entityId: string }>({
      query: ({ entityId }) => ({
        url: `${BASE(entityId)}/enrollment/`,
      }),
      providesTags: ["ESTIMATED_TAX_ENROLLMENT"],
    }),

    enrollSelf: build.mutation<
      EstimatedTaxEnrollment,
      { entityId: string; trigger?: number }
    >({
      query: ({ entityId, trigger }) => ({
        url: `${BASE(entityId)}/enrollment/`,
        method: "POST",
        body: trigger !== undefined ? { trigger } : {},
      }),
      invalidatesTags: ["ESTIMATED_TAX_ENROLLMENT", "ESTIMATED_TAX_WATERFALL"],
    }),

    // ── Settings (NOL + §174) ─────────────────────────────────────────────
    getEstimatedTaxSettings: build.query<
      EstimatedTaxSettings,
      { entityId: string }
    >({
      query: ({ entityId }) => ({
        url: `${BASE(entityId)}/settings/`,
      }),
      providesTags: ["ESTIMATED_TAX_SETTINGS"],
    }),

    updateEstimatedTaxSettings: build.mutation<
      EstimatedTaxSettings,
      { entityId: string; payload: UpdateSettingsPayload }
    >({
      query: ({ entityId, payload }) => ({
        url: `${BASE(entityId)}/settings/`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["ESTIMATED_TAX_SETTINGS", "ESTIMATED_TAX_WATERFALL"],
    }),

    // ── IRS Payments ──────────────────────────────────────────────────────
    getIRSPayments: build.query<
      IRSPayment[],
      { entityId: string; year: string }
    >({
      query: ({ entityId, year }) => ({
        url: `${BASE(entityId)}/payments/?year=${year}`,
      }),
      providesTags: ["IRS_PAYMENTS"],
    }),

    logIRSPayment: build.mutation<
      IRSPayment,
      { entityId: string; payload: LogPaymentPayload }
    >({
      query: ({ entityId, payload }) => ({
        url: `${BASE(entityId)}/payments/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["IRS_PAYMENTS", "ESTIMATED_TAX_WATERFALL"],
    }),

    updateIRSPayment: build.mutation<
      IRSPayment,
      {
        entityId: string;
        paymentId: string;
        payload: Partial<LogPaymentPayload>;
      }
    >({
      query: ({ entityId, paymentId, payload }) => ({
        url: `${BASE(entityId)}/payments/${paymentId}/`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["IRS_PAYMENTS", "ESTIMATED_TAX_WATERFALL"],
    }),

    deleteIRSPayment: build.mutation<
      { success: boolean },
      { entityId: string; paymentId: string }
    >({
      query: ({ entityId, paymentId }) => ({
        url: `${BASE(entityId)}/payments/${paymentId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["IRS_PAYMENTS", "ESTIMATED_TAX_WATERFALL"],
    }),

    // ── Monthly manual entries (per-month admin override) ─────────────────
    setMonthlyEntry: build.mutation<
      MonthlyManualEntry,
      { entityId: string; payload: SetMonthlyEntryPayload }
    >({
      query: ({ entityId, payload }) => ({
        url: `${BASE(entityId)}/monthly-entries/`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["ESTIMATED_TAX_WATERFALL"],
    }),

    deleteMonthlyEntry: build.mutation<
      { success: boolean },
      { entityId: string; year: string; month: number }
    >({
      query: ({ entityId, year, month }) => ({
        url: `${BASE(entityId)}/monthly-entries/`,
        method: "DELETE",
        body: { year, month },
      }),
      invalidatesTags: ["ESTIMATED_TAX_WATERFALL"],
    }),

    // ── §174 Entries ──────────────────────────────────────────────────────
    getS174Entries: build.query<S174Entry[], { entityId: string }>({
      query: ({ entityId }) => ({
        url: `${BASE(entityId)}/s174-entries/`,
      }),
      providesTags: ["ESTIMATED_TAX_S174"],
    }),

    addS174Entry: build.mutation<
      S174Entry,
      { entityId: string; payload: AddS174EntryPayload }
    >({
      query: ({ entityId, payload }) => ({
        url: `${BASE(entityId)}/s174-entries/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        "ESTIMATED_TAX_S174",
        "ESTIMATED_TAX_SETTINGS",
        "ESTIMATED_TAX_WATERFALL",
      ],
    }),

    updateS174Entry: build.mutation<
      S174Entry,
      { entityId: string; entryId: string; payload: UpdateS174EntryPayload }
    >({
      query: ({ entityId, entryId, payload }) => ({
        url: `${BASE(entityId)}/s174-entries/${entryId}/`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: [
        "ESTIMATED_TAX_S174",
        "ESTIMATED_TAX_SETTINGS",
        "ESTIMATED_TAX_WATERFALL",
      ],
    }),

    deleteS174Entry: build.mutation<
      { success: boolean },
      { entityId: string; entryId: string }
    >({
      query: ({ entityId, entryId }) => ({
        url: `${BASE(entityId)}/s174-entries/${entryId}/`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "ESTIMATED_TAX_S174",
        "ESTIMATED_TAX_SETTINGS",
        "ESTIMATED_TAX_WATERFALL",
      ],
    }),

    // ── Tax Adjustments ───────────────────────────────────────────────────
    getMonthlyAdjustmentBreakdown: build.query<
      MonthlyAdjustmentBreakdown[],
      { entityId: string; taxYear: string }
    >({
      query: ({ entityId, taxYear }) => ({
        url: `${BASE(
          entityId
        )}/adjustments/monthly-breakdown/?tax_year=${taxYear}`,
      }),
      providesTags: ["ESTIMATED_TAX_ADJUSTMENTS"],
    }),

    getAdjustment: build.query<
      TaxAdjustment,
      { entityId: string; adjustmentId: string }
    >({
      query: ({ entityId, adjustmentId }) => ({
        url: `${BASE(entityId)}/adjustments/${adjustmentId}/`,
      }),
    }),

    listAdjustments: build.query<
      TaxAdjustment[],
      { entityId: string; taxYear: number }
    >({
      query: ({ entityId, taxYear }) => ({
        url: `${BASE(entityId)}/adjustments/?tax_year=${taxYear}`,
      }),
      providesTags: ["ESTIMATED_TAX_ADJUSTMENTS"],
    }),

    createAdjustment: build.mutation<
      TaxAdjustment,
      { entityId: string; payload: CreateAdjustmentPayload }
    >({
      query: ({ entityId, payload }) => ({
        url: `${BASE(entityId)}/adjustments/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["ESTIMATED_TAX_ADJUSTMENTS", "ESTIMATED_TAX_WATERFALL"],
    }),

    updateAdjustment: build.mutation<
      TaxAdjustment,
      {
        entityId: string;
        adjustmentId: string;
        payload: UpdateAdjustmentPayload;
      }
    >({
      query: ({ entityId, adjustmentId, payload }) => ({
        url: `${BASE(entityId)}/adjustments/${adjustmentId}/`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["ESTIMATED_TAX_ADJUSTMENTS", "ESTIMATED_TAX_WATERFALL"],
    }),

    deleteAdjustment: build.mutation<
      { success: boolean },
      { entityId: string; adjustmentId: string }
    >({
      query: ({ entityId, adjustmentId }) => ({
        url: `${BASE(entityId)}/adjustments/${adjustmentId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["ESTIMATED_TAX_ADJUSTMENTS", "ESTIMATED_TAX_WATERFALL"],
    }),

    // ── Financial Adjustments ─────────────────────────────────────────────
    listFinancialAdjustments: build.query<
      FinancialAdjustment[],
      { entityId: string; year: string; month?: string }
    >({
      query: ({ entityId, year, month }) => {
        const params = new URLSearchParams({ year });
        if (month) params.set("month", month);
        return {
          url: `${BASE(entityId)}/financial-adjustments/?${params}`,
        };
      },
      providesTags: ["ESTIMATED_TAX_FINANCIAL_ADJUSTMENTS"],
    }),

    createFinancialAdjustment: build.mutation<
      FinancialAdjustment,
      { entityId: string; payload: CreateFinancialAdjustmentPayload }
    >({
      query: ({ entityId, payload }) => ({
        url: `${BASE(entityId)}/financial-adjustments/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        "ESTIMATED_TAX_FINANCIAL_ADJUSTMENTS",
        "ESTIMATED_TAX_WATERFALL",
      ],
    }),

    updateFinancialAdjustment: build.mutation<
      FinancialAdjustment,
      {
        entityId: string;
        adjustmentId: string;
        payload: UpdateFinancialAdjustmentPayload;
      }
    >({
      query: ({ entityId, adjustmentId, payload }) => ({
        url: `${BASE(entityId)}/financial-adjustments/${adjustmentId}/`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: [
        "ESTIMATED_TAX_FINANCIAL_ADJUSTMENTS",
        "ESTIMATED_TAX_WATERFALL",
      ],
    }),

    deleteFinancialAdjustment: build.mutation<
      { success: boolean },
      { entityId: string; adjustmentId: string }
    >({
      query: ({ entityId, adjustmentId }) => ({
        url: `${BASE(entityId)}/financial-adjustments/${adjustmentId}/`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "ESTIMATED_TAX_FINANCIAL_ADJUSTMENTS",
        "ESTIMATED_TAX_WATERFALL",
      ],
    }),

    // ── Tax Benefits ──────────────────────────────────────────────────────
    getTaxBenefits: build.query<TaxBenefitsResponse, { entityId: string }>({
      query: ({ entityId }) => ({
        url: `${BASE(entityId)}/tax-benefits/`,
      }),
      providesTags: ["ESTIMATED_TAX_BENEFITS"],
    }),

    addBenefitEntry: build.mutation<
      TaxBenefit,
      { entityId: string; payload: AddBenefitEntryPayload }
    >({
      query: ({ entityId, payload }) => ({
        url: `${BASE(entityId)}/benefit-entries/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["ESTIMATED_TAX_BENEFITS", "ESTIMATED_TAX_WATERFALL"],
    }),

    deleteBenefitEntry: build.mutation<
      TaxBenefit,
      { entityId: string; entryId: string }
    >({
      query: ({ entityId, entryId }) => ({
        url: `${BASE(entityId)}/benefit-entries/${entryId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["ESTIMATED_TAX_BENEFITS", "ESTIMATED_TAX_WATERFALL"],
      async onQueryStarted({ entityId }, { dispatch, queryFulfilled }) {
        const { data: updatedBenefit } = await queryFulfilled;
        if (!updatedBenefit?.category) return;
        dispatch(
          extendedApi.util.updateQueryData(
            "getTaxBenefits",
            { entityId },
            (draft) => {
              const index = draft.benefits.findIndex(
                (b) => b.category === updatedBenefit.category
              );
              if (index !== -1) draft.benefits[index] = updatedBenefit;
            }
          )
        );
      },
    }),

    // ── §174 Benefit Entries (Tax Benefits screen — separate table from Settings' §174 Entries) ──
    getS174BenefitEntries: build.query<S174Entry[], { entityId: string }>({
      query: ({ entityId }) => ({
        url: `${BASE(entityId)}/s174-benefit-entries/`,
      }),
      providesTags: ["ESTIMATED_TAX_BENEFITS"],
    }),

    addS174BenefitEntry: build.mutation<
      S174Entry,
      { entityId: string; payload: AddS174EntryPayload }
    >({
      query: ({ entityId, payload }) => ({
        url: `${BASE(entityId)}/s174-benefit-entries/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["ESTIMATED_TAX_BENEFITS", "ESTIMATED_TAX_WATERFALL"],
    }),

    deleteS174BenefitEntry: build.mutation<
      { success: boolean },
      { entityId: string; entryId: string }
    >({
      query: ({ entityId, entryId }) => ({
        url: `${BASE(entityId)}/s174-benefit-entries/${entryId}/`,
        method: "DELETE",
      }),
      // A 404 means the row was already deleted or replaced, so refetch anyway.
      invalidatesTags: (result, error) =>
        result || error?.status === 404
          ? ["ESTIMATED_TAX_BENEFITS", "ESTIMATED_TAX_WATERFALL"]
          : [],
    }),

    uploadTaxReturn: build.mutation<
      UploadTaxReturnResult,
      { entityId: string; formData: FormData }
    >({
      query: ({ entityId, formData }) => ({
        url: `${BASE(entityId)}/tax-benefits/upload-return/`,
        method: "POST",
        body: formData,
        formData: true,
      }),
      // A CONFLICT response saved nothing, so there is nothing to refresh.
      invalidatesTags: (result) =>
        result && result.rnd_status !== "CONFLICT"
          ? ["ESTIMATED_TAX_BENEFITS", "ESTIMATED_TAX_WATERFALL"]
          : [],
    }),

    // ── Send to Chat ──────────────────────────────────────────────────────
    sendQuarterToChat: build.mutation<
      {
        success: boolean;
        channel_url: string;
        card_content: {
          message_text: string;
          metadata: Record<string, unknown>;
        };
      },
      { entityId: string; quarter: number; year: number }
    >({
      query: ({ entityId, quarter, year }) => ({
        url: `${BASE(entityId)}/send-to-chat/`,
        method: "POST",
        body: { quarter, year },
      }),
    }),

    // ── Send card + message to Stream ────────────────────────────────────────
    sendCardToStream: build.mutation<
      { success: boolean },
      { entityId: string; quarter: number; year: number; message_text: string }
    >({
      query: ({ entityId, quarter, year, message_text }) => ({
        url: `${BASE(entityId)}/send-card-to-stream/`,
        method: "POST",
        body: { quarter, year, message_text },
      }),
    }),

    // ── Sync config (admin only) ───────────────────────────────────────────
    setSyncConfig: build.mutation<
      SyncConfig,
      { entityId: string; payload: SetSyncConfigPayload }
    >({
      query: ({ entityId, payload }) => ({
        url: `${BASE(entityId)}/sync-config/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["ESTIMATED_TAX_WATERFALL"],
    }),

    clearSyncConfig: build.mutation<
      { success: boolean },
      { entityId: string; payload: ClearSyncConfigPayload }
    >({
      query: ({ entityId, payload }) => ({
        url: `${BASE(entityId)}/sync-config/`,
        method: "DELETE",
        body: payload,
      }),
      invalidatesTags: ["ESTIMATED_TAX_WATERFALL"],
    }),

    // ── Income Statement Upload ───────────────────────────────────────────
    uploadIncomeStatement: build.mutation<
      IncomeStatementUploadResult,
      { entityId: string; formData: FormData }
    >({
      query: ({ entityId, formData }) => ({
        url: `${BASE(entityId)}/income-statement-upload/`,
        method: "POST",
        body: formData,
        formData: true,
      }),
      invalidatesTags: [
        "ESTIMATED_TAX_WATERFALL",
        "INCOME_STATEMENT_UPLOAD",
        "Tasks",
      ],
    }),

    getIncomeStatementUpload: build.query<
      IncomeStatementUploadResult | Record<string, never>,
      { entityId: string; taxYear: string }
    >({
      query: ({ entityId, taxYear }) => ({
        url: `${BASE(
          entityId
        )}/income-statement-upload/detail/?tax_year=${taxYear}`,
      }),
      providesTags: ["INCOME_STATEMENT_UPLOAD"],
    }),

    // ── Export Monthly Breakdown ───────────────────────────────────────────
    exportMonthlyBreakdown: build.query<
      { excel_document_id?: string },
      { entityId: string; year: string }
    >({
      query: ({ entityId, year }) => ({
        url: `${BASE(entityId)}/waterfall/export/?year=${year}`,
      }),
    }),

    deleteIncomeStatementUpload: build.mutation<
      { success: boolean },
      { entityId: string; taxYear: string }
    >({
      query: ({ entityId, taxYear }) => ({
        url: `${BASE(entityId)}/income-statement-upload/detail/`,
        method: "DELETE",
        body: { tax_year: taxYear },
      }),
      invalidatesTags: ["ESTIMATED_TAX_WATERFALL", "INCOME_STATEMENT_UPLOAD"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMonthlyAdjustmentBreakdownQuery,
  useGetAdjustmentQuery,
  useGetEstimatedTaxWaterfallQuery,
  useGetEnrollmentQuery,
  useEnrollSelfMutation,
  useGetEstimatedTaxSettingsQuery,
  useUpdateEstimatedTaxSettingsMutation,
  useGetIRSPaymentsQuery,
  useLogIRSPaymentMutation,
  useUpdateIRSPaymentMutation,
  useDeleteIRSPaymentMutation,
  useSetMonthlyEntryMutation,
  useDeleteMonthlyEntryMutation,
  useGetS174EntriesQuery,
  useAddS174EntryMutation,
  useUpdateS174EntryMutation,
  useDeleteS174EntryMutation,
  useSetSyncConfigMutation,
  useClearSyncConfigMutation,
  useListAdjustmentsQuery,
  useCreateAdjustmentMutation,
  useUpdateAdjustmentMutation,
  useDeleteAdjustmentMutation,
  useListFinancialAdjustmentsQuery,
  useCreateFinancialAdjustmentMutation,
  useUpdateFinancialAdjustmentMutation,
  useDeleteFinancialAdjustmentMutation,
  useSendQuarterToChatMutation,
  useSendCardToStreamMutation,
  useUploadIncomeStatementMutation,
  useGetIncomeStatementUploadQuery,
  useDeleteIncomeStatementUploadMutation,
  useLazyExportMonthlyBreakdownQuery,
  useGetTaxBenefitsQuery,
  useAddBenefitEntryMutation,
  useDeleteBenefitEntryMutation,
  useGetS174BenefitEntriesQuery,
  useAddS174BenefitEntryMutation,
  useDeleteS174BenefitEntryMutation,
  useUploadTaxReturnMutation,
} = extendedApi;
